/**
 * MFA-required interceptor for the locked 13-stack (slot 10).
 *
 * Sits BELOW `enrichAuthContext` (slot 4) so `call.auth.userActor` is
 * already populated. For any RPC method in `methods`, enforces:
 *
 *   1. `call.auth.userActor` is present (otherwise the caller is an
 *      S2S-only / worker-only client invoking a user-gated method —
 *      reject as `mfa_required:user_actor_missing`).
 *   2. `userActor.amr[]` includes at least one of the MFA-equivalent
 *      methods: `mfa`, `totp`, `webauthn` (RFC 8176 conventions),
 *      case-insensitive.
 *   3. `userActor.iat` is a finite integer (a missing / NaN / non-integer
 *      `iat` on a gated method is a hard reject — `mfa_iat_invalid` — never
 *      a fail-open accept).
 *   4. `now - userActor.iat <= freshnessSeconds` — the user JWT was
 *      issued recently enough to satisfy the policy. Stale MFA assertions
 *      get re-prompted via the `auth-sdk` re-auth flow (`mfa_stale`).
 *   5. `userActor.iat - now <= clockSkewSeconds` — the assertion is NOT
 *      dated in the future beyond a small clock-skew tolerance. Without
 *      this clamp a future-dated `iat` yields a negative age that trivially
 *      satisfies check 4, so the MFA proof would be accepted indefinitely
 *      (`mfa_future_iat`). FAIL-CLOSED.
 *
 * On any failure → throws PERMISSION_DENIED with the canonical detail
 * `auth_mfa_required:<reason>` (wire-stable across all language ports).
 *
 * Per `01-communication-doctrine § 6` + `06-library-integration § 4.3`,
 * `mfaRequiredInterceptor` is the ONLY way to enforce step-up MFA on a
 * gRPC method — services do NOT inline-check `amr` in handler code.
 */
import { status } from "@grpc/grpc-js";
import type { ServerUnaryCallWithAuth } from "../types/auth-context";
import { grpcError } from "./grpc-error";
import type { UnaryValueHandler } from "./with-auth-unary-value";

/** MFA-equivalent values per RFC 8176 + Nodii usage. */
const MFA_AMR_VALUES = new Set(["mfa", "totp", "webauthn", "u2f"]);

export type MfaRequiredOptions = {
  /**
   * Set of fully-qualified gRPC method names that require fresh MFA.
   * Match is exact-string against the `methodName` the 13-stack
   * composer passes through.
   */
  methods: ReadonlySet<string> | readonly string[];
  /**
   * Max age (seconds) of the user JWT's `iat` for the MFA assertion
   * to count as "fresh". Recommended: 300s (5 min) for high-risk
   * operations, 3600s (1h) for medium-risk.
   */
  freshnessSeconds: number;
  /**
   * Allowed clock-skew tolerance (seconds) for a future-dated `iat`.
   * An MFA assertion whose `iat` is more than this many seconds AHEAD of
   * `now` is rejected as `mfa_future_iat` (fail-closed against forged /
   * future-dated proofs and badly-skewed issuers). Must be a non-negative
   * integer. Defaults to `DEFAULT_CLOCK_SKEW_SECONDS` (60s).
   */
  clockSkewSeconds?: number;
  /**
   * Clock injection (epoch seconds). Defaults to
   * `Math.floor(Date.now()/1000)`. Tests inject a fixed clock.
   */
  nowFn?: () => number;
};

/** Default future-`iat` clock-skew tolerance (seconds). */
export const DEFAULT_CLOCK_SKEW_SECONDS = 60;

function asSet(
  v: ReadonlySet<string> | readonly string[],
): ReadonlySet<string> {
  return v instanceof Set ? v : new Set(v);
}

function hasMfaAmr(amr: string[] | undefined): boolean {
  if (!amr) return false;
  for (const v of amr) {
    if (typeof v === "string" && MFA_AMR_VALUES.has(v.toLowerCase())) {
      return true;
    }
  }
  return false;
}

/**
 * Build the MFA-required wrapper. Apply to an inner handler at slot 10
 * of the 13-stack composer:
 *
 *   ```ts
 *   const mfaGate = mfaRequiredInterceptor({
 *     methods: new Set([
 *       "billing.InvoiceService/RefundInvoice",
 *       "billing.PaymentMethodService/RemovePaymentMethod",
 *     ]),
 *     freshnessSeconds: 300,
 *   });
 *
 *   const wrapped = mfaGate("billing.InvoiceService/RefundInvoice", inner);
 *   ```
 */
export function mfaRequiredInterceptor(opts: MfaRequiredOptions) {
  const methods = asSet(opts.methods);
  const freshnessSeconds = opts.freshnessSeconds;
  const clockSkewSeconds = opts.clockSkewSeconds ?? DEFAULT_CLOCK_SKEW_SECONDS;
  const nowFn = opts.nowFn ?? (() => Math.floor(Date.now() / 1000));

  if (!Number.isInteger(freshnessSeconds) || freshnessSeconds <= 0) {
    throw new Error(
      "mfaRequiredInterceptor: freshnessSeconds must be a positive integer",
    );
  }
  if (!Number.isInteger(clockSkewSeconds) || clockSkewSeconds < 0) {
    throw new Error(
      "mfaRequiredInterceptor: clockSkewSeconds must be a non-negative integer",
    );
  }

  return <Req, Res>(
    methodName: string,
    handler: UnaryValueHandler<Req, Res>,
  ): UnaryValueHandler<Req, Res> => {
    if (!methods.has(methodName)) {
      return handler; // pass-through; no MFA gate on this method
    }

    return async (call: ServerUnaryCallWithAuth<Req, Res>): Promise<Res> => {
      const userActor = call.auth?.userActor;
      if (!userActor) {
        throw grpcError(
          status.PERMISSION_DENIED,
          `mfa required for ${methodName}: user_actor_missing`,
          "auth_mfa_required:user_actor_missing",
        );
      }
      if (!hasMfaAmr(userActor.amr)) {
        throw grpcError(
          status.PERMISSION_DENIED,
          `mfa required for ${methodName}: mfa_amr_missing`,
          "auth_mfa_required:mfa_amr_missing",
        );
      }
      // FAIL-CLOSED: a missing / NaN / non-integer iat on a gated method is
      // never accepted. Without this, `NaN > freshnessSeconds` is `false`,
      // which would silently fail open.
      const iat = userActor.iat;
      if (typeof iat !== "number" || !Number.isFinite(iat)) {
        throw grpcError(
          status.PERMISSION_DENIED,
          `mfa required for ${methodName}: mfa_iat_invalid`,
          "auth_mfa_required:mfa_iat_invalid",
        );
      }
      const now = nowFn();
      // Reject future-dated assertions beyond the skew tolerance. A future
      // iat produces a negative age that would trivially pass the staleness
      // check below, accepting a forged / future-dated MFA proof forever.
      const futureSkew = iat - now;
      if (futureSkew > clockSkewSeconds) {
        throw grpcError(
          status.PERMISSION_DENIED,
          `mfa required for ${methodName}: mfa_future_iat (ahead=${futureSkew}s, maxSkew=${clockSkewSeconds}s)`,
          "auth_mfa_required:mfa_future_iat",
        );
      }
      const ageSeconds = now - iat;
      if (ageSeconds > freshnessSeconds) {
        throw grpcError(
          status.PERMISSION_DENIED,
          `mfa required for ${methodName}: mfa_stale (age=${ageSeconds}s, max=${freshnessSeconds}s)`,
          "auth_mfa_required:mfa_stale",
        );
      }

      return handler(call);
    };
  };
}
